import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, Trash, Gear, Tag, DotsSixVertical, FloppyDisk, Buildings, Files, Clock, Calendar, Lock, Bell } from '@phosphor-icons/react';
import { FasesPanel } from '@/components/configuracion/FasesPanel';
import { NotificacionesPanel } from '@/components/notifications/NotificacionesPanel';
import { PlantillasPage } from '@/pages/PlantillasPage';
import { useCatalogs } from '@/hooks/useTasks';
import {
  useCapacidadDefaults,
  useUpsertCapacidadDefault,
  useEffectiveCapacidad,
  useUpsertCapacidadOverride,
  useDeleteCapacidadOverride,
} from '@/hooks/useCapacidades';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Sprint } from '@/types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useCategorias,
  useCreateCategoria,
  useUpdateCategoria,
  useDeleteCategoria,
  useReorderCategorias,
} from '@/hooks/useCategorias';
import { api } from '@/api/client';
import type { Category } from '@/types';

type SettingsTab = 'categorias' | 'fases' | 'plantillas' | 'capacidades' | 'sprints' | 'notificaciones';

export function ConfiguracionPage() {
  // Plantillas arranca por defecto: es el panel mas usado, asi que evita un
  // click extra cada vez que se abre Configuracion.
  const [activeTab, setActiveTab] = useState<SettingsTab>('plantillas');

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="px-4 py-3 border-b border-stone-200 bg-white flex items-center gap-4">
        <h1 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
          <Gear size={20} weight="bold" />
          Configuración
        </h1>

        <div className="ml-2 flex items-center gap-1">
          <SettingsTabBtn
            id="plantillas"
            label="Plantillas"
            icon={<Files size={14} weight="bold" />}
            active={activeTab === 'plantillas'}
            onClick={() => setActiveTab('plantillas')}
          />
          <SettingsTabBtn
            id="categorias"
            label="Categorías"
            icon={<Tag size={14} weight="bold" />}
            active={activeTab === 'categorias'}
            onClick={() => setActiveTab('categorias')}
          />
          <SettingsTabBtn
            id="fases"
            label="Fases y Estados"
            icon={<Buildings size={14} weight="bold" />}
            active={activeTab === 'fases'}
            onClick={() => setActiveTab('fases')}
          />
          <SettingsTabBtn
            id="capacidades"
            label="Capacidades"
            icon={<Clock size={14} weight="bold" />}
            active={activeTab === 'capacidades'}
            onClick={() => setActiveTab('capacidades')}
          />
          <SettingsTabBtn
            id="sprints"
            label="Sprints"
            icon={<Calendar size={14} weight="bold" />}
            active={activeTab === 'sprints'}
            onClick={() => setActiveTab('sprints')}
          />
          <SettingsTabBtn
            id="notificaciones"
            label="Notificaciones"
            icon={<Bell size={14} weight="bold" />}
            active={activeTab === 'notificaciones'}
            onClick={() => setActiveTab('notificaciones')}
          />
        </div>
      </div>

      {/* Plantillas trae su propio header + master-detail, asi que renderiza
          a pantalla completa (sin el padding del wrapper). Los otros paneles
          usan el contenedor scrollable normal. */}
      {activeTab === 'plantillas' ? (
        <div className="flex-1 overflow-hidden">
          <PlantillasPage />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'categorias' && <CategoriasPanel />}
          {activeTab === 'fases' && <FasesPanel />}
          {activeTab === 'capacidades' && <CapacidadesPanel />}
          {activeTab === 'sprints' && <SprintsPanel />}
          {activeTab === 'notificaciones' && (
            <div className="max-w-2xl">
              <NotificacionesPanel />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsTabBtn({
  id, label, icon, active, onClick,
}: {
  id: string; label: string; icon: React.ReactNode;
  active: boolean; onClick: () => void;
}) {
  return (
    <button
      key={id}
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1.5 ${
        active
          ? 'bg-brand-green-100/30 text-stone-900 font-semibold'
          : 'text-stone-600 hover:bg-stone-100'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ============================================================
// CategoriasPanel - lista con DnD reorder + edit inline + crear/borrar.
// ============================================================
function CategoriasPanel() {
  const { data: categorias = [], isLoading } = useCategorias();
  const reorderMut = useReorderCategorias();
  const [showNew, setShowNew] = useState(false);

  // Local copy for optimistic DnD; sync when server data changes.
  const [localItems, setLocalItems] = useState<Category[]>(categorias);
  useEffect(() => { setLocalItems(categorias); }, [categorias]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = localItems.findIndex(c => c.idCatTareaArq === active.id);
    const newIdx = localItems.findIndex(c => c.idCatTareaArq === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(localItems, oldIdx, newIdx);
    setLocalItems(next);
    reorderMut.mutate(next.map(c => c.idCatTareaArq));
  }

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-stone-900 flex items-center gap-2">
            <Tag size={16} weight="bold" />
            Categorías de tareas
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Editá nombre, abreviatura y color. Arrastrá los items para cambiar el orden.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-black bg-brand-green-100 hover:bg-brand-green-200 rounded"
        >
          <Plus size={14} weight="bold" /> Nueva categoría
        </button>
      </div>

      <div className="bg-white rounded-lg border border-stone-200">
        {isLoading && (
          <div className="px-4 py-6 text-sm text-stone-500">Cargando…</div>
        )}
        {!isLoading && localItems.length === 0 && (
          <div className="px-4 py-6 text-sm text-stone-500 italic">
            Sin categorías. Creá una con el botón de arriba.
          </div>
        )}

        {localItems.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localItems.map(c => c.idCatTareaArq)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-stone-100">
                {localItems.map(cat => (
                  <CategoriaRow key={cat.idCatTareaArq} category={cat} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {showNew && <NewCategoriaDialog onClose={() => setShowNew(false)} />}
    </div>
  );
}

// ============================================================
// CategoriaRow - drag handle + color swatch (color picker inline) +
// nombre y abreviatura editables on blur + boton borrar.
// ============================================================
function CategoriaRow({ category }: { category: Category }) {
  const updateMut = useUpdateCategoria();
  const deleteMut = useDeleteCategoria();

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: category.idCatTareaArq });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [nombre, setNombre] = useState(category.nombre || '');
  const [abrev, setAbrev] = useState(category.abreviatura || '');
  const [color, setColor] = useState(category.colorHex || '#888780');

  // Re-sync when server data changes (e.g. after a reorder refetch).
  useEffect(() => {
    setNombre(category.nombre || '');
    setAbrev(category.abreviatura || '');
    setColor(category.colorHex || '#888780');
  }, [category.nombre, category.abreviatura, category.colorHex]);

  function commitNombre() {
    const trimmed = nombre.trim();
    if (!trimmed) { setNombre(category.nombre || ''); return; } // no permitir vacio
    if (trimmed === category.nombre) return;
    updateMut.mutate({ id: category.idCatTareaArq, dto: { nombre: trimmed } });
  }
  function commitAbrev() {
    const trimmed = abrev.trim();
    if (trimmed === (category.abreviatura || '')) return;
    updateMut.mutate({ id: category.idCatTareaArq, dto: { abreviatura: trimmed || null } });
  }
  function commitColor() {
    if (color === (category.colorHex || '')) return;
    updateMut.mutate({ id: category.idCatTareaArq, dto: { colorHex: color } });
  }

  async function handleDelete() {
    // Pre-check de uso para no asustar con un confirm si esta en uso.
    try {
      const usage = await api.categories.usage(category.idCatTareaArq);
      if (usage.tareas > 0 || usage.plantillas > 0) {
        window.alert(
          `No se puede eliminar "${category.nombre}": ${usage.tareas} tarea(s) y ${usage.plantillas} plantilla(s) la usan.`
        );
        return;
      }
    } catch { /* fallback al confirm normal */ }

    if (!window.confirm(`¿Eliminar la categoría "${category.nombre}"?`)) return;
    try {
      await deleteMut.mutateAsync(category.idCatTareaArq);
    } catch (err: any) {
      window.alert(err?.message || 'No se pudo eliminar');
    }
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-3 py-2 group ${isDragging ? 'bg-stone-100' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="opacity-30 group-hover:opacity-100 text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing transition-opacity p-0.5 flex-shrink-0"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar para reordenar"
      >
        <DotsSixVertical size={16} weight="bold" />
      </button>

      <input
        type="color"
        value={color}
        onChange={e => setColor(e.target.value)}
        onBlur={commitColor}
        title="Color de la categoría"
        className="w-7 h-7 rounded cursor-pointer border border-stone-200 flex-shrink-0"
      />

      <input
        value={nombre}
        onChange={e => setNombre(e.target.value)}
        onBlur={commitNombre}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        placeholder="Nombre"
        className="flex-1 bg-transparent border-0 px-2 py-1 text-sm rounded hover:bg-stone-100 focus:bg-white focus:ring-2 focus:ring-brand-green-100 focus:outline-none"
      />

      <input
        value={abrev}
        onChange={e => setAbrev(e.target.value.slice(0, 10))}
        onBlur={commitAbrev}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        placeholder="Abrev."
        maxLength={10}
        className="w-24 bg-transparent border-0 px-2 py-1 text-xs rounded hover:bg-stone-100 focus:bg-white focus:ring-2 focus:ring-brand-green-100 focus:outline-none"
      />

      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteMut.isPending}
        className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition-opacity p-1 rounded hover:bg-stone-100 flex-shrink-0 disabled:opacity-30"
        title="Eliminar categoría"
        aria-label="Eliminar categoría"
      >
        <Trash size={14} weight="bold" />
      </button>
    </li>
  );
}

// ============================================================
// NewCategoriaDialog - modal compacto. Tras crear cierra.
// ============================================================
function NewCategoriaDialog({ onClose }: { onClose: () => void }) {
  const createMut = useCreateCategoria();
  const [nombre, setNombre] = useState('');
  const [abrev, setAbrev] = useState('');
  const [color, setColor] = useState('#888780');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const canSubmit = nombre.trim().length > 0;

  async function handleSubmit() {
    if (!canSubmit) return;
    await createMut.mutateAsync({
      nombre: nombre.trim(),
      abreviatura: abrev.trim() || null,
      colorHex: color,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-md p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Tag size={16} weight="bold" />
            Nueva categoría
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Nombre</label>
          <input
            ref={inputRef}
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
            placeholder="Ej. Permisos"
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>

        <div className="grid grid-cols-[1fr_120px] gap-3">
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Abreviatura</label>
            <input
              value={abrev}
              onChange={e => setAbrev(e.target.value.slice(0, 10))}
              maxLength={10}
              placeholder="Hasta 10 chars"
              className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Color</label>
            <input
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              className="w-full h-9 rounded cursor-pointer border border-stone-200"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || createMut.isPending}
            className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <FloppyDisk size={12} weight="bold" />
            {createMut.isPending ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CapacidadesPanel - default por usuario + selector de sprint para overrides.
// Edicion inline: el numero se commitea on blur o Enter. Default 40h si el
// usuario no tiene fila (se crea al primer commit).
// ============================================================
function CapacidadesPanel() {
  const { data: catalogs } = useCatalogs();
  const { data: defaults = [] } = useCapacidadDefaults();

  // Sprint para ver/editar overrides. Default = sprint vigente del store
  // (mismo que el TopBar usa). Sin sprint elegido NO mostramos columna de
  // override — solo el default por usuario.
  const semanalVigente = catalogs?.sprints.find(
    (s) => s.tipo === 'Semanal'
      && s.activo
      && s.fechaInicio && s.fechaFin
      && s.fechaInicio.slice(0, 10) <= new Date().toISOString().slice(0, 10)
      && s.fechaFin.slice(0, 10)   >= new Date().toISOString().slice(0, 10)
  );
  const [selectedSprintId, setSelectedSprintId] = useState<number | null>(
    semanalVigente?.idSprint ?? null
  );

  // Cuando carga catalogs y todavia no elegimos sprint, defaultea al vigente.
  useEffect(() => {
    if (selectedSprintId === null && semanalVigente) {
      setSelectedSprintId(semanalVigente.idSprint);
    }
  }, [semanalVigente, selectedSprintId]);

  const { data: effective = [] } = useEffectiveCapacidad(selectedSprintId);

  const defaultsByCol = useMemo(() => {
    const m = new Map<number, typeof defaults[number]>();
    defaults.forEach((d) => m.set(d.idCol, d));
    return m;
  }, [defaults]);

  const effectiveByCol = useMemo(() => {
    const m = new Map<number, typeof effective[number]>();
    effective.forEach((e) => m.set(e.idCol, e));
    return m;
  }, [effective]);

  const users = catalogs?.users ?? [];

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-base font-semibold text-stone-900 flex items-center gap-2">
            <Clock size={16} weight="bold" />
            Capacidad de horas por colaborador
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Default por usuario (47.5h en una semana normal) menos las horas de
            revisión = disponible para tareas. Override por sprint para
            vacaciones / semanas cortas / part-time temporal.
          </p>
        </div>

        <div className="shrink-0">
          <label className="block text-[11px] font-medium text-stone-500 uppercase mb-1">
            Sprint para overrides
          </label>
          <select
            value={selectedSprintId ?? ''}
            onChange={(e) =>
              setSelectedSprintId(e.target.value ? Number(e.target.value) : null)
            }
            className="px-3 py-1.5 border border-stone-300 rounded text-sm bg-white min-w-[260px] focus:outline-none focus:ring-2 focus:ring-brand-green-100"
          >
            <option value="">— Sin sprint (solo defaults) —</option>
            {(catalogs?.sprints ?? []).map((s) => (
              <option key={s.idSprint} value={s.idSprint}>{s.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200 text-xs uppercase text-stone-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Colaborador</th>
              <th className="text-right px-4 py-2 font-medium w-28">Default (h)</th>
              <th className="text-right px-4 py-2 font-medium w-28">Revisión (h)</th>
              {selectedSprintId !== null && (
                <>
                  <th className="text-right px-4 py-2 font-medium w-32">Override sprint</th>
                  <th className="text-right px-4 py-2 font-medium w-40">Asignadas / Disp.</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {users.length === 0 && (
              <tr>
                <td colSpan={selectedSprintId !== null ? 5 : 3} className="px-4 py-6 text-center text-stone-500 italic">
                  Cargando colaboradores…
                </td>
              </tr>
            )}
            {users.map((u) => {
              const def = defaultsByCol.get(u.idCol);
              const eff = effectiveByCol.get(u.idCol);
              return (
                <CapacidadRow
                  key={u.idCol}
                  idCol={u.idCol}
                  nombre={u.nombreCompleto || u.nombre || `#${u.idCol}`}
                  defaultHoras={def?.horasDefault ?? null}
                  revisionHoras={def?.horasRevision ?? null}
                  selectedSprintId={selectedSprintId}
                  effective={eff ?? null}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Una fila: default editable + (cuando hay sprint elegido) override editable
// y horas asignadas read-only.
function CapacidadRow({
  idCol, nombre, defaultHoras, revisionHoras, selectedSprintId, effective,
}: {
  idCol: number;
  nombre: string;
  defaultHoras: number | null;
  revisionHoras: number | null;
  selectedSprintId: number | null;
  effective: {
    horasEfectivas: number;
    tieneOverride: boolean;
    horasAsignadas: number;
    horasRevision: number;
    horasDisponibles: number;
  } | null;
}) {
  const upsertDefault = useUpsertCapacidadDefault();
  const upsertOverride = useUpsertCapacidadOverride();
  const deleteOverride = useDeleteCapacidadOverride();

  const [defValue, setDefValue] = useState(defaultHoras !== null ? String(defaultHoras) : '');
  const [revValue, setRevValue] = useState(revisionHoras !== null ? String(revisionHoras) : '');
  const [ovValue, setOvValue] = useState(
    effective?.tieneOverride ? String(effective.horasEfectivas) : ''
  );

  useEffect(() => {
    setDefValue(defaultHoras !== null ? String(defaultHoras) : '');
  }, [defaultHoras]);

  useEffect(() => {
    setRevValue(revisionHoras !== null ? String(revisionHoras) : '');
  }, [revisionHoras]);

  useEffect(() => {
    setOvValue(effective?.tieneOverride ? String(effective.horasEfectivas) : '');
  }, [effective?.tieneOverride, effective?.horasEfectivas]);

  // Parsea un input de horas (0-168); si es inválido devuelve el fallback.
  function parseHoras(s: string, fallback: number): number {
    const n = parseFloat(s.trim().replace(',', '.'));
    return isNaN(n) || n < 0 || n > 168 ? fallback : n;
  }

  // El endpoint upsert escribe AMBOS campos (default + revisión), así que al
  // editar uno mandamos el valor actual local del otro para no pisarlo.
  function commitDefault() {
    const trimmed = defValue.trim().replace(',', '.');
    if (trimmed === '') {
      setDefValue(defaultHoras !== null ? String(defaultHoras) : '');
      return;
    }
    const n = parseFloat(trimmed);
    if (isNaN(n) || n < 0 || n > 168) {
      setDefValue(defaultHoras !== null ? String(defaultHoras) : '');
      return;
    }
    if (defaultHoras !== null && n === defaultHoras) return;
    upsertDefault.mutate({ idCol, horasDefault: n, horasRevision: parseHoras(revValue, revisionHoras ?? 5) });
  }

  function commitRevision() {
    const trimmed = revValue.trim().replace(',', '.');
    if (trimmed === '') {
      setRevValue(revisionHoras !== null ? String(revisionHoras) : '');
      return;
    }
    const n = parseFloat(trimmed);
    if (isNaN(n) || n < 0 || n > 168) {
      setRevValue(revisionHoras !== null ? String(revisionHoras) : '');
      return;
    }
    if (revisionHoras !== null && n === revisionHoras) return;
    upsertDefault.mutate({ idCol, horasDefault: parseHoras(defValue, defaultHoras ?? 47.5), horasRevision: n });
  }

  function commitOverride() {
    if (selectedSprintId === null) return;
    const trimmed = ovValue.trim().replace(',', '.');

    if (trimmed === '') {
      // Vacio + tenia override -> borrar override (vuelve al default)
      if (effective?.tieneOverride) {
        deleteOverride.mutate({ idSprint: selectedSprintId, idCol });
      }
      return;
    }
    const n = parseFloat(trimmed);
    if (isNaN(n) || n < 0 || n > 168) {
      setOvValue(effective?.tieneOverride ? String(effective.horasEfectivas) : '');
      return;
    }
    if (effective?.tieneOverride && n === effective.horasEfectivas) return;
    upsertOverride.mutate({ idSprint: selectedSprintId, idCol, horasOverride: n });
  }

  // Indicador de carga: horas asignadas vs DISPONIBLES (bruta − revisión).
  const asignadas = effective?.horasAsignadas ?? 0;
  const efectivas = effective?.horasEfectivas ?? 0;
  const revisionEff = effective?.horasRevision ?? 0;
  const disponibles = effective?.horasDisponibles ?? 0;
  const ratio = disponibles > 0 ? asignadas / disponibles : 0;
  const ratioColor =
    ratio >= 1 ? 'text-red-600 font-semibold' :
    ratio >= 0.8 ? 'text-amber-600' :
    'text-stone-700';

  return (
    <tr className="hover:bg-stone-50">
      <td className="px-4 py-2 text-stone-800">{nombre}</td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          min={0}
          max={168}
          step={0.5}
          value={defValue}
          onChange={(e) => setDefValue(e.target.value)}
          onBlur={commitDefault}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="—"
          className="w-20 text-right bg-stone-100 border-0 rounded px-2 py-1 text-sm focus:bg-white focus:ring-2 focus:ring-brand-green-100"
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          min={0}
          max={168}
          step={0.5}
          value={revValue}
          onChange={(e) => setRevValue(e.target.value)}
          onBlur={commitRevision}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="—"
          title="Horas por sprint reservadas para revisión/autorización (se restan de la capacidad)"
          className="w-20 text-right bg-stone-100 border-0 rounded px-2 py-1 text-sm focus:bg-white focus:ring-2 focus:ring-brand-green-100"
        />
      </td>
      {selectedSprintId !== null && (
        <>
          <td className="px-4 py-2 text-right">
            <input
              type="number"
              min={0}
              max={168}
              step={0.5}
              value={ovValue}
              onChange={(e) => setOvValue(e.target.value)}
              onBlur={commitOverride}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              placeholder={defValue || '—'}
              title={effective?.tieneOverride ? 'Vacio = borrar override' : 'Override solo aplica a este sprint'}
              className={`w-24 text-right border-0 rounded px-2 py-1 text-sm focus:bg-white focus:ring-2 focus:ring-brand-green-100 ${
                effective?.tieneOverride ? 'bg-amber-50' : 'bg-stone-100'
              }`}
            />
          </td>
          <td
            className={`px-4 py-2 text-right font-mono ${ratioColor}`}
            title={`Bruta ${efectivas.toFixed(1)}h − revisión ${revisionEff.toFixed(1)}h = disponible ${disponibles.toFixed(1)}h`}
          >
            {asignadas.toFixed(1)} / {disponibles.toFixed(1)}
          </td>
        </>
      )}
    </tr>
  );
}

// ============================================================
// SprintsPanel - CRUD de sprints especiales + read-only de semanales.
// El sprint cero "Sin Asignar" y los semanales (creados por el Timer
// Function) aparecen con icono de candado y no son editables aca. Los
// especiales se pueden crear, renombrar, ajustar fechas y desactivar.
// ============================================================
function SprintsPanel() {
  const qc = useQueryClient();
  const { data: sprints = [], isLoading } = useQuery({
    queryKey: ['sprints', { activos: undefined }],
    queryFn: () => api.sprints.list({}),
  });

  const [showNew, setShowNew] = useState(false);

  const createMut = useMutation({
    mutationFn: (dto: { nombre: string; fechaInicio: string; fechaFin: string }) =>
      api.sprints.create(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sprints'] });
      qc.invalidateQueries({ queryKey: ['catalogs'] });
      setShowNew(false);
    },
  });

  // Orden visual: SinAsignar primero, luego Semanal por inicio desc,
  // luego Especial por inicio desc. Ya viene asi del backend, mantenemos.
  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-stone-900 flex items-center gap-2">
            <Calendar size={16} weight="bold" />
            Sprints
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Los <strong>semanales</strong> los crea automáticamente el Timer cada lunes 06:00 CR.
            Aquí podés crear <strong>especiales</strong> (rangos ad-hoc) y editar los existentes.
            El sprint cero <strong>Sin Asignar</strong> no se puede modificar.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-black bg-brand-green-100 hover:bg-brand-green-200 rounded"
        >
          <Plus size={14} weight="bold" /> Nuevo especial
        </button>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        {isLoading && (
          <div className="px-4 py-6 text-sm text-stone-500">Cargando…</div>
        )}
        {!isLoading && sprints.length === 0 && (
          <div className="px-4 py-6 text-sm text-stone-500 italic">
            Sin sprints.
          </div>
        )}
        {sprints.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-xs uppercase text-stone-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Nombre</th>
                <th className="text-left px-4 py-2 font-medium w-28">Tipo</th>
                <th className="text-left px-4 py-2 font-medium w-32">Inicio</th>
                <th className="text-left px-4 py-2 font-medium w-32">Fin</th>
                <th className="text-center px-4 py-2 font-medium w-24">Activo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {sprints.map((s) => (
                <SprintRow key={s.idSprint} sprint={s} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewSprintDialog
          busy={createMut.isPending}
          onClose={() => setShowNew(false)}
          onCreate={(nombre, fechaInicio, fechaFin) =>
            createMut.mutate({ nombre, fechaInicio, fechaFin })
          }
        />
      )}
    </div>
  );
}

function SprintRow({ sprint }: { sprint: Sprint }) {
  const qc = useQueryClient();
  const isReadonly = sprint.tipo !== 'Especial';

  const [nombre, setNombre] = useState(sprint.nombre);
  const [fechaInicio, setFechaInicio] = useState(sprint.fechaInicio?.slice(0, 10) ?? '');
  const [fechaFin, setFechaFin] = useState(sprint.fechaFin?.slice(0, 10) ?? '');

  useEffect(() => {
    setNombre(sprint.nombre);
    setFechaInicio(sprint.fechaInicio?.slice(0, 10) ?? '');
    setFechaFin(sprint.fechaFin?.slice(0, 10) ?? '');
  }, [sprint]);

  const updateMut = useMutation({
    mutationFn: (dto: { nombre?: string; fechaInicio?: string; fechaFin?: string; activo?: boolean }) =>
      api.sprints.update(sprint.idSprint, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sprints'] });
      qc.invalidateQueries({ queryKey: ['catalogs'] });
    },
    onError: (e: any) => {
      alert(e?.message || 'No se pudo guardar');
      setNombre(sprint.nombre);
      setFechaInicio(sprint.fechaInicio?.slice(0, 10) ?? '');
      setFechaFin(sprint.fechaFin?.slice(0, 10) ?? '');
    },
  });

  function commitNombre() {
    const trimmed = nombre.trim();
    if (!trimmed || trimmed === sprint.nombre) { setNombre(sprint.nombre); return; }
    updateMut.mutate({ nombre: trimmed });
  }
  function commitFechaInicio() {
    if (!fechaInicio || fechaInicio === sprint.fechaInicio?.slice(0, 10)) return;
    updateMut.mutate({ fechaInicio });
  }
  function commitFechaFin() {
    if (!fechaFin || fechaFin === sprint.fechaFin?.slice(0, 10)) return;
    updateMut.mutate({ fechaFin });
  }

  const tipoLabel = sprint.tipo === 'SinAsignar' ? 'Sin Asignar' : sprint.tipo;
  const tipoColor =
    sprint.tipo === 'SinAsignar' ? 'bg-stone-200 text-stone-700' :
    sprint.tipo === 'Semanal'    ? 'bg-blue-100 text-blue-800' :
                                   'bg-amber-100 text-amber-800';

  return (
    <tr className="hover:bg-stone-50">
      <td className="px-4 py-2">
        {isReadonly ? (
          <span className="flex items-center gap-1.5 text-stone-700">
            <Lock size={12} weight="bold" className="text-stone-400" />
            {sprint.nombre}
          </span>
        ) : (
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onBlur={commitNombre}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            className="w-full bg-transparent border-0 px-2 py-1 text-sm rounded hover:bg-stone-100 focus:bg-white focus:ring-2 focus:ring-brand-green-100 focus:outline-none"
          />
        )}
      </td>
      <td className="px-4 py-2">
        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full ${tipoColor}`}>
          {tipoLabel}
        </span>
      </td>
      <td className="px-4 py-2">
        {sprint.tipo === 'SinAsignar' ? (
          <span className="text-stone-400 italic text-xs">—</span>
        ) : isReadonly ? (
          <span className="font-mono text-xs text-stone-600">{fechaInicio}</span>
        ) : (
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            onBlur={commitFechaInicio}
            className="bg-stone-100 border-0 rounded px-2 py-1 text-xs font-mono focus:ring-2 focus:ring-brand-green-100"
          />
        )}
      </td>
      <td className="px-4 py-2">
        {sprint.tipo === 'SinAsignar' ? (
          <span className="text-stone-400 italic text-xs">—</span>
        ) : isReadonly ? (
          <span className="font-mono text-xs text-stone-600">{fechaFin}</span>
        ) : (
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            onBlur={commitFechaFin}
            className="bg-stone-100 border-0 rounded px-2 py-1 text-xs font-mono focus:ring-2 focus:ring-brand-green-100"
          />
        )}
      </td>
      <td className="px-4 py-2 text-center">
        {isReadonly ? (
          <span className={`inline-block w-2 h-2 rounded-full ${sprint.activo ? 'bg-emerald-500' : 'bg-stone-300'}`} title={sprint.activo ? 'Activo' : 'Inactivo'} />
        ) : (
          <input
            type="checkbox"
            checked={sprint.activo}
            onChange={(e) => updateMut.mutate({ activo: e.target.checked })}
            className="cursor-pointer accent-brand-green-100"
            title="Activar / desactivar"
          />
        )}
      </td>
    </tr>
  );
}

function NewSprintDialog({
  busy, onClose, onCreate,
}: {
  busy: boolean;
  onClose: () => void;
  onCreate: (nombre: string, fechaInicio: string, fechaFin: string) => void;
}) {
  // Defaults: rango lun-dom de la PROXIMA semana (caso comun de planeacion).
  const today = new Date();
  const daysSinceMonday = ((today.getDay() - 1 + 7) % 7);
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() - daysSinceMonday + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const [nombre, setNombre] = useState(`Sprint ${fmt(nextMonday)}`);
  const [fechaInicio, setFechaInicio] = useState(fmt(nextMonday));
  const [fechaFin, setFechaFin] = useState(fmt(nextSunday));

  const canSubmit = nombre.trim().length > 0 && fechaInicio !== '' && fechaFin !== '' && fechaFin >= fechaInicio;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Calendar size={16} weight="bold" />
            Nuevo sprint especial
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Nombre</label>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) onCreate(nombre.trim(), fechaInicio, fechaFin); }}
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Inicio</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Fin</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50">
            Cancelar
          </button>
          <button
            onClick={() => onCreate(nombre.trim(), fechaInicio, fechaFin)}
            disabled={!canSubmit || busy}
            className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <FloppyDisk size={12} weight="bold" />
            {busy ? 'Creando…' : 'Crear sprint'}
          </button>
        </div>
      </div>
    </div>
  );
}

